import { getLoanDisplayListing } from 'app/lib/loan-display-listing';
import { useTheme } from '@shopify/restyle';
import {
  IconAlertCircle,
  IconCalendarOff,
  IconChevronLeft,
  IconChevronRight,
  IconClock,
  IconRotateClockwise,
} from '@tabler/icons-react-native';
import { ListingThumbnail } from 'app/components/listing-thumbnail';
import { Column, Row } from 'app/design/primitives/box';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';
import { useNavigation } from 'app/hooks/navigation';
import { addDays, isSameDay, localeAwareFormat, startOfDay } from 'app/lib/date';
import { getImageVariantUrl } from 'app/lib/image-variants';
import { navigateToLoan } from 'app/screens/root-stack-helper';
import type { Theme } from 'app/shopify';
import type { Loan } from 'app/types/loan';
import { getLoanETA, LoanETA, LoanType } from 'app/types/loan';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { differenceInCalendarDays, startOfWeek } from 'date-fns';
import { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type DimensionValue,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  Pressable,
  ScrollView,
  View,
} from 'react-native';
import Reanimated, {
  ReduceMotion,
  type SharedValue,
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';
import { getLoanRangeEnd, loanOccursOnDate } from './loan-calendar-utils';
import {
  CALENDAR_INITIAL_PAGE,
  CALENDAR_PAGE_COUNT,
  getCalendarPageIndex,
  getLoanTimelineContinuation,
  getLoanTimelineLanePosition,
  getResponsivePlannerLayout,
  getVisiblePlannerRangeStart,
  groupLoansByListing,
} from './responsive-loan-planner-utils';

type PlannerGroup = 'attention' | 'ongoing' | 'upcoming' | 'completed';

interface ResponsiveLoanPlannerProps {
  loans: Loan[];
  loanType: LoanType;
}

interface PlannerPage {
  dates: Date[];
  groups: {
    group: PlannerGroup;
    count: number;
    listings: {
      id: string;
      loans: Loan[];
      minimumHeight: number;
      height: number;
    }[];
  }[];
  visibleLoans: Loan[];
}

const CALENDAR_PAGES = Array.from({ length: CALENDAR_PAGE_COUNT }, (_, index) => index);

const PLANNER_GROUPS: PlannerGroup[] = ['attention', 'ongoing', 'upcoming', 'completed'];

function getSizing(theme: Theme, token: keyof Theme['tokens']['sizing']) {
  return Number(theme.tokens.sizing[token]);
}

function getPlannerGroup(loan: Loan): PlannerGroup {
  const eta = getLoanETA(loan);

  if (eta === LoanETA.late || eta === LoanETA.returned) return 'attention';
  if (eta === LoanETA.ongoing) return 'ongoing';
  if (eta === LoanETA.scheduled || eta === LoanETA.pending) return 'upcoming';
  return 'completed';
}

function getGroupPriority(group: PlannerGroup) {
  return PLANNER_GROUPS.indexOf(group);
}

function getGroupAccent(group: PlannerGroup, theme: Theme) {
  if (group === 'attention') return theme.colors['surface.error.default'];
  if (group === 'ongoing') return theme.colors['surface.success.default'];
  if (group === 'upcoming') return theme.colors['surface.info.default'];
  return theme.colors['text.secondary'];
}

function getLoanSubtle(loan: Loan, theme: Theme) {
  const eta = getLoanETA(loan);

  if (eta === LoanETA.late) return theme.colors['surface.error.subtle'];
  if (eta === LoanETA.returned) return theme.colors['surface.warning.subtle'];
  if (eta === LoanETA.scheduled || eta === LoanETA.pending) return theme.colors['surface.info.subtle'];
  if (eta === LoanETA.past) return theme.colors['surface.primary-neutral'];
  return theme.colors['surface.success.subtle'];
}

function formatLoanRange(loan: Loan, dateFormat: string) {
  const start = new Date(loan.start);
  const end = getLoanRangeEnd(loan);

  if (start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()) {
    if (isSameDay(start, end)) return localeAwareFormat(start, dateFormat);
    return `${localeAwareFormat(start, 'd')}–${localeAwareFormat(end, dateFormat)}`;
  }

  return `${localeAwareFormat(start, dateFormat)} – ${localeAwareFormat(end, dateFormat)}`;
}

function formatRangeLabel(start: Date, end: Date) {
  if (start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()) {
    return `${localeAwareFormat(start, 'd')}–${localeAwareFormat(end, 'd MMM y')}`;
  }

  if (start.getFullYear() === end.getFullYear()) {
    return `${localeAwareFormat(start, 'd MMM')}–${localeAwareFormat(end, 'd MMM y')}`;
  }

  return `${localeAwareFormat(start, 'd MMM y')}–${localeAwareFormat(end, 'd MMM y')}`;
}

function getDominantGroup(loans: Loan[]) {
  return loans.reduce<PlannerGroup>((dominant, loan) => {
    const loanGroup = getPlannerGroup(loan);
    return getGroupPriority(loanGroup) < getGroupPriority(dominant) ? loanGroup : dominant;
  }, 'completed');
}

function TimelineBackground({ dates }: { dates: Date[] }) {
  const theme = useTheme<Theme>();
  const today = startOfDay(new Date());

  return (
    <Row pointerEvents="none" style={{ bottom: 0, left: 0, position: 'absolute', right: 0, top: 0 }}>
      {dates.map((date) => {
        const isToday = isSameDay(date, today);

        return (
          <View
            key={date.toISOString()}
            style={{
              backgroundColor: isToday
                ? theme.colors['surface.brand.subtle']
                : date.getDay() === 0 || date.getDay() === 6
                  ? theme.colors['surface.primary-neutral']
                  : theme.colors['surface.default'],
              borderLeftColor: theme.colors['border.secondary'],
              borderLeftWidth: theme.borderWidths['border-width.subtle'],
              flex: 1,
            }}
          />
        );
      })}
    </Row>
  );
}

function LoanDurationIndicator({ dates, loan }: { dates: Date[]; loan: Loan }) {
  const theme = useTheme<Theme>();
  const today = startOfDay(new Date());

  return (
    <Row pointerEvents="none" style={{ bottom: 0, left: 0, position: 'absolute', right: 0, top: 0 }}>
      {dates.map((date, index) => {
        const isActive = loanOccursOnDate(loan, date, today);

        if (!isActive) return <View key={date.toISOString()} style={{ flex: 1 }} />;

        const continuesBefore = loanOccursOnDate(loan, addDays(date, -1), today);
        const continuesAfter = loanOccursOnDate(loan, addDays(date, 1), today);

        return (
          <View key={date.toISOString()} style={{ flex: 1, justifyContent: 'flex-end' }}>
            <View
              style={{
                backgroundColor: getLoanSubtle(loan, theme),
                borderBottomLeftRadius: index > 0 && continuesBefore ? 0 : theme.borderRadii['radius.xs'],
                borderBottomRightRadius:
                  index < dates.length - 1 && continuesAfter ? 0 : theme.borderRadii['radius.xs'],
                borderTopLeftRadius: index > 0 && continuesBefore ? 0 : theme.borderRadii['radius.xs'],
                borderTopRightRadius: index < dates.length - 1 && continuesAfter ? 0 : theme.borderRadii['radius.xs'],
                height: theme.spacing['spacing.general.x1'],
              }}
            />
          </View>
        );
      })}
    </Row>
  );
}

function GroupHeading({ count, group }: { count: number; group: PlannerGroup }) {
  const theme = useTheme<Theme>();
  const { t } = useTranslation('loans');
  const accent = getGroupAccent(group, theme);
  const Icon = group === 'attention' ? IconAlertCircle : group === 'ongoing' ? IconRotateClockwise : IconClock;

  return (
    <Row
      alignItems="center"
      backgroundColor="surface.primary-neutral"
      gap="spacing.general.x1"
      paddingHorizontal="spacing.general.x2"
      style={{ height: getSizing(theme, 'sizing.general.x12') }}
    >
      <Icon color={accent} size={14} strokeWidth={ICON_STROKE_WIDTH} />
      <Column flex={1} minWidth={0}>
        <Text numberOfLines={2} variant="bodyXSmallMedium">
          {t(`calendar.planner.groups.${group}`)}
        </Text>
        <Text color="text.secondary" variant="bodyCaption">
          {count}
        </Text>
      </Column>
    </Row>
  );
}

function UserAvatar({ size, user }: { size: number; user: Loan['borrower'] }) {
  const theme = useTheme<Theme>();
  const picture = user.profile?.picture;

  if (picture) {
    return (
      <PCImage
        aspectRatio={1}
        height={size}
        source={{ uri: getImageVariantUrl(picture, 'mini') }}
        style={{ borderRadius: theme.borderRadii['radius.full'] }}
      />
    );
  }

  const initials = user.profile
    ? `${user.profile.firstName.charAt(0)}${user.profile.lastName.charAt(0)}`.toUpperCase()
    : '?';

  return (
    <View
      style={{
        alignItems: 'center',
        backgroundColor: theme.colors['surface.brand.subtle'],
        borderRadius: theme.borderRadii['radius.full'],
        height: size,
        justifyContent: 'center',
        width: size,
      }}
    >
      <Text color="text.brand" variant="bodyCaptionStrong">
        {initials}
      </Text>
    </View>
  );
}

function LoanTimelineLabel({
  dates,
  loan,
  loanType,
  labelLeft,
  labelWidth,
  scrollX,
  pageOffset,
}: {
  dates: Date[];
  loan: Loan;
  loanType: LoanType;
  labelLeft: number;
  labelWidth: number;
  scrollX: SharedValue<number>;
  pageOffset: number;
}) {
  const theme = useTheme<Theme>();
  const { t } = useTranslation('loans');
  const otherUser = loanType === LoanType.Borrowing ? loan.lender : loan.borrower;
  const otherUserName = otherUser.profile
    ? `${otherUser.profile.firstName} ${otherUser.profile.lastName}`
    : t('deletedUser', { ns: 'common' });
  const dateFormat = t('dateFormat_MMMMd', { ns: 'common' });
  const { width } = getLoanTimelineLanePosition(dates, loan);
  const visibleDaySpan = Math.round((width / 100) * dates.length);
  const isCompact = visibleDaySpan <= 1;
  const isCondensed = visibleDaySpan === 2;
  const pinnedStyle = useAnimatedStyle(
    () => ({ transform: [{ translateX: scrollX.value - pageOffset }] }),
    [pageOffset],
  );

  return (
    <Reanimated.View
      pointerEvents="none"
      testID={`calendar-loan-label-${loan.id}`}
      style={[
        {
          position: 'absolute',
          left: labelLeft,
          width: labelWidth,
          height: Math.min(getSizing(theme, 'sizing.general.x18'), getSizing(theme, 'sizing.general.x14')),
          padding: theme.spacing['spacing.general.x1'],
          overflow: 'hidden',
        },
        pinnedStyle,
      ]}
    >
      <Row
        alignItems="center"
        style={{ width: '100%', height: '100%' }}
        gap={isCompact ? undefined : 'spacing.general.x2'}
        justifyContent={isCompact ? 'center' : 'flex-start'}
        paddingHorizontal={isCompact ? 'spacing.general.x1' : 'spacing.general.x2'}
        paddingVertical="spacing.general.x1"
      >
        <UserAvatar size={getSizing(theme, 'sizing.general.x7')} user={otherUser} />
        {!isCompact ? (
          <Column flex={1} minWidth={0}>
            <Text numberOfLines={1} variant="bodySmallMedium">
              {otherUserName}
            </Text>
            {!isCondensed ? (
              <Text color="text.secondary" numberOfLines={1} variant="bodyCaption">
                {formatLoanRange(loan, dateFormat)}
              </Text>
            ) : null}
          </Column>
        ) : null}
        {!isCompact && !isCondensed ? (
          <IconChevronRight
            color={theme.colors['text.secondary']}
            size={getSizing(theme, 'sizing.icon.sm')}
            strokeWidth={ICON_STROKE_WIDTH}
          />
        ) : null}
      </Row>
    </Reanimated.View>
  );
}

function LoanTimelineLane({
  dates,
  loan,
  loanType,
  scrollX,
  viewportWidth,
  pageOffset,
}: {
  dates: Date[];
  loan: Loan;
  loanType: LoanType;
  scrollX: SharedValue<number>;
  viewportWidth: number;
  pageOffset: number;
}) {
  const theme = useTheme<Theme>();
  const { t } = useTranslation('loans');
  const navigation = useNavigation();
  const eta = getLoanETA(loan);
  const otherUser = loanType === LoanType.Borrowing ? loan.lender : loan.borrower;
  const otherUserName = otherUser.profile
    ? `${otherUser.profile.firstName} ${otherUser.profile.lastName}`
    : t('deletedUser', { ns: 'common' });
  const dateFormat = t('dateFormat_MMMMd', { ns: 'common' });
  const { left, width } = getLoanTimelineLanePosition(dates, loan);
  const continuation = getLoanTimelineContinuation(dates, loan);
  const labelLeft = 0;
  const labelWidth = (width / 100) * viewportWidth;
  // The native scroll offset cancels the bar's motion. The bar itself clips the
  // stationary label as either edge passes it, without JS-driven layout changes.

  return (
    <View
      style={{
        flexShrink: 0,
        height: getSizing(theme, 'sizing.general.x18'),
        maxHeight: getSizing(theme, 'sizing.general.x14'),
        overflow: 'hidden',
        position: 'relative',
        left: `${String(left)}%` as DimensionValue,
        width: `${String(width)}%` as DimensionValue,
      }}
    >
      <Pressable
        accessibilityLabel={`${otherUserName}, ${formatLoanRange(loan, dateFormat)}, ${t(`card.status.${eta}`)}`}
        accessibilityRole="button"
        onPress={() => navigateToLoan(navigation, loan.id, loan.listing.id)}
        style={({ pressed }) => ({
          bottom: 0,
          left: 0,
          opacity: pressed ? 0.65 : 1,
          position: 'absolute',
          right: 0,
          top: 0,
        })}
      >
        <Row
          testID={`calendar-loan-bar-${loan.id}`}
          style={{
            alignItems: 'center',
            backgroundColor: getLoanSubtle(loan, theme),
            borderColor: theme.colors['border.secondary'],
            borderTopLeftRadius: continuation.before ? 0 : theme.borderRadii['radius.sm'],
            borderBottomLeftRadius: continuation.before ? 0 : theme.borderRadii['radius.sm'],
            borderTopRightRadius: continuation.after ? 0 : theme.borderRadii['radius.sm'],
            borderBottomRightRadius: continuation.after ? 0 : theme.borderRadii['radius.sm'],
            borderWidth: theme.borderWidths['border-width.standard'],
            borderLeftWidth: continuation.before ? 0 : theme.borderWidths['border-width.standard'],
            borderRightWidth: continuation.after ? 0 : theme.borderWidths['border-width.standard'],
            bottom: theme.spacing['spacing.general.x1'],
            left: continuation.before ? 0 : theme.spacing['spacing.general.x1'],
            overflow: 'hidden',
            position: 'absolute',
            right: continuation.after ? 0 : theme.spacing['spacing.general.x1'],
            top: theme.spacing['spacing.general.x1'],
          }}
        >
          <LoanTimelineLabel
            dates={dates}
            loan={loan}
            loanType={loanType}
            labelLeft={labelLeft}
            labelWidth={labelWidth}
            scrollX={scrollX}
            pageOffset={pageOffset}
          />
          <View style={{ height: theme.spacing['spacing.general.x1'], position: 'relative' }}>
            <LoanDurationIndicator dates={dates} loan={loan} />
          </View>
        </Row>
      </Pressable>
    </View>
  );
}

function ListingDetails({
  detailsWidth,
  loans,
  minimumHeight,
  onHeight,
}: {
  detailsWidth: number;
  loans: Loan[];
  minimumHeight: number;
  onHeight: (height: number) => void;
}) {
  const theme = useTheme<Theme>();
  const { t } = useTranslation('loans');
  const listing = getLoanDisplayListing(loans[0]!);
  const dominantGroup = getDominantGroup(loans);
  const nextLoan = loans.find((loan) => {
    const eta = getLoanETA(loan);
    return eta === LoanETA.scheduled || eta === LoanETA.pending;
  });
  const compactStatus =
    dominantGroup === 'attention'
      ? t('calendar.planner.compact.attention')
      : dominantGroup === 'ongoing'
        ? t('calendar.planner.compact.ongoing')
        : dominantGroup === 'upcoming' && nextLoan
          ? localeAwareFormat(new Date(nextLoan.start), 'd MMM')
          : t('calendar.planner.compact.completed');
  const summary = `${t('calendar.planner.compact.loanCount', { count: loans.length })} · ${compactStatus}`;

  return (
    <Column
      testID={`calendar-listing-${listing.id}`}
      onLayout={(event) => onHeight(Math.ceil(event.nativeEvent.layout.height))}
      alignItems="center"
      gap="spacing.general.x2"
      justifyContent="center"
      paddingHorizontal="spacing.general.x2"
      paddingVertical="spacing.general.x3"
      style={{
        borderLeftColor: getGroupAccent(dominantGroup, theme),
        borderLeftWidth: theme.borderWidths['border-width.emphasized'],
        borderRightColor: theme.colors['border.secondary'],
        borderRightWidth: theme.borderWidths['border-width.subtle'],
        minHeight: minimumHeight,
        width: detailsWidth,
      }}
    >
      <ListingThumbnail
        hideCorpoBadge
        size={getSizing(theme, 'sizing.general.x16')}
        {...listing}
        isFound={Boolean(listing.lender)}
      />

      <Column alignItems="center" minWidth={0} width="100%">
        <Text numberOfLines={2} textAlign="center" variant="bodySmallMedium">
          {listing.item.name}
        </Text>
        <Row alignItems="center" gap="spacing.general.x1" justifyContent="center" width="100%">
          <View
            style={{
              backgroundColor: getGroupAccent(dominantGroup, theme),
              borderRadius: theme.borderRadii['radius.full'],
              height: theme.spacing['spacing.general.x2'],
              width: theme.spacing['spacing.general.x2'],
            }}
          />

          <Text color="text.secondary" numberOfLines={1} variant="bodyXSmall">
            {summary}
          </Text>
        </Row>
      </Column>
    </Column>
  );
}

function PlannerDateHeader({ dates, today, dayWidth }: { dates: Date[]; today: Date; dayWidth: number }) {
  const theme = useTheme<Theme>();
  return (
    <Row style={{ height: 56 }}>
      {dates.map((date) => {
        const isToday = isSameDay(date, today);

        return (
          <View
            key={date.toISOString()}
            style={{
              alignItems: 'center',
              backgroundColor: isToday
                ? theme.colors['surface.brand.subtle']
                : date.getDay() === 0 || date.getDay() === 6
                  ? theme.colors['surface.primary-neutral']
                  : theme.colors['surface.default'],
              borderLeftColor: theme.colors['border.secondary'],
              borderLeftWidth: theme.borderWidths['border-width.subtle'],
              width: dayWidth,
              justifyContent: 'center',
            }}
          >
            <Text color="text.secondary" numberOfLines={1} variant="bodyXSmallMedium">
              {localeAwareFormat(date, 'EEE')}
            </Text>
            <View
              style={{
                alignItems: 'center',
                backgroundColor: isToday ? theme.colors['surface.brand.subtle'] : 'transparent',
                borderRadius: theme.borderRadii['radius.full'],
                height: getSizing(theme, 'sizing.general.x7'),
                justifyContent: 'center',
                width: getSizing(theme, 'sizing.general.x7'),
              }}
            >
              <Text
                color={isToday ? 'text.brand' : 'text.secondary'}
                variant={isToday ? 'bodyLargeStrong' : 'bodyLargeMedium'}
              >
                {date.getDate()}
              </Text>
            </View>
          </View>
        );
      })}
    </Row>
  );
}

function PlannerTimelinePage({
  page,
  pageIndex,
  isActive,
  timelineWidth,
  dayWidth,
  today,
  loanType,
  scrollX,
}: {
  page: PlannerPage;
  pageIndex: number;
  isActive: boolean;
  timelineWidth: number;
  dayWidth: number;
  today: Date;
  loanType: LoanType;
  scrollX: SharedValue<number>;
}) {
  const theme = useTheme<Theme>();
  const { t } = useTranslation('loans');
  return (
    <Column
      testID={`calendar-page-${pageIndex}`}
      style={{ width: timelineWidth }}
      accessibilityElementsHidden={!isActive}
      importantForAccessibility={isActive ? 'auto' : 'no-hide-descendants'}
    >
      <PlannerDateHeader dates={page.dates} today={today} dayWidth={dayWidth} />
      {page.groups.map(({ group, listings }) => (
        <Column key={group}>
          <View
            style={{
              height: getSizing(theme, 'sizing.general.x12'),
              backgroundColor: theme.colors['surface.primary-neutral'],
            }}
          />
          {listings.map(({ id, loans: listingLoans, height }) => (
            <Column
              key={id}
              justifyContent="center"
              testID={`calendar-grid-row-${pageIndex}-${id}`}
              style={{
                height,
                position: 'relative',
                borderTopWidth: theme.borderWidths['border-width.subtle'],
                borderTopColor: theme.colors['border.secondary'],
              }}
            >
              <TimelineBackground dates={page.dates} />
              {listingLoans.map((loan) => (
                <LoanTimelineLane
                  key={loan.id}
                  dates={page.dates}
                  loan={loan}
                  loanType={loanType}
                  scrollX={scrollX}
                  viewportWidth={timelineWidth}
                  pageOffset={pageIndex * timelineWidth}
                />
              ))}
            </Column>
          ))}
        </Column>
      ))}
      {!page.groups.length ? (
        <View style={{ height: 180, position: 'relative' }} testID={`calendar-empty-grid-${pageIndex}`}>
          <TimelineBackground dates={page.dates} />
          <Column
            alignItems="center"
            justifyContent="center"
            gap="spacing.general.x2"
            style={{ width: timelineWidth, height: 180, padding: 12 }}
          >
            <IconCalendarOff color={theme.colors['text.secondary']} size={24} strokeWidth={ICON_STROKE_WIDTH} />
            <Text textAlign="center" variant="bodySmallMedium">
              {t('calendar.emptyRange.title')}
            </Text>
            <Text color="text.secondary" textAlign="center" variant="bodyXSmall">
              {t('calendar.emptyRange.description')}
            </Text>
          </Column>
        </View>
      ) : null}
    </Column>
  );
}

function PlannerRangeNavigation({
  visibleRangeStart,
  rangeEnd,
  onPrevious,
  onToday,
  onNext,
}: {
  visibleRangeStart: Date;
  rangeEnd: Date;
  onPrevious: () => void;
  onToday: () => void;
  onNext: () => void;
}) {
  const theme = useTheme<Theme>();
  const { t } = useTranslation('loans');
  return (
    <Row
      alignItems="center"
      justifyContent="space-between"
      paddingHorizontal="spacing.general.x2"
      paddingVertical="spacing.general.x1"
    >
      <Pressable
        accessibilityLabel={t('calendar.previousPeriod')}
        accessibilityRole="button"
        hitSlop={theme.spacing['spacing.general.x2']}
        onPress={onPrevious}
        style={({ pressed }) => ({
          alignItems: 'center',
          backgroundColor: pressed ? theme.colors['state-layer.press'] : theme.colors['surface.primary-neutral'],
          borderRadius: theme.borderRadii['radius.full'],
          height: 44,
          justifyContent: 'center',
          width: 44,
        })}
      >
        <IconChevronLeft color={theme.colors['text.primary']} size={20} strokeWidth={ICON_STROKE_WIDTH} />
      </Pressable>

      <Pressable
        accessibilityRole="button"
        onPress={onToday}
        style={({ pressed }) => ({
          minHeight: 44,
          justifyContent: 'center',
          alignItems: 'center',
          opacity: pressed ? 0.55 : 1,
        })}
      >
        <Text variant="bodyBaseMedium">{formatRangeLabel(visibleRangeStart, rangeEnd)}</Text>
      </Pressable>

      <Pressable
        accessibilityLabel={t('calendar.nextPeriod')}
        accessibilityRole="button"
        hitSlop={theme.spacing['spacing.general.x2']}
        onPress={onNext}
        style={({ pressed }) => ({
          alignItems: 'center',
          backgroundColor: pressed ? theme.colors['state-layer.press'] : theme.colors['surface.primary-neutral'],
          borderRadius: theme.borderRadii['radius.full'],
          height: 44,
          justifyContent: 'center',
          width: 44,
        })}
      >
        <IconChevronRight color={theme.colors['text.primary']} size={20} strokeWidth={ICON_STROKE_WIDTH} />
      </Pressable>
    </Row>
  );
}

function PlannerListingColumn({
  calendarGroups,
  visibleLoanCount,
  detailsWidth,
  onRowHeight,
}: {
  calendarGroups: PlannerPage['groups'];
  visibleLoanCount: number;
  detailsWidth: number;
  onRowHeight: (id: string, height: number) => void;
}) {
  const theme = useTheme<Theme>();
  const { t } = useTranslation('loans');
  return (
    <Column style={{ width: detailsWidth }} testID="calendar-fixed-listings">
      <Column
        justifyContent="center"
        paddingHorizontal="spacing.general.x3"
        style={{
          height: 56,
          borderTopWidth: theme.borderWidths['border-width.subtle'],
          borderTopColor: theme.colors['border.secondary'],
        }}
      >
        <Text numberOfLines={1} variant="bodySmallMedium">
          {t('calendar.planner.loanColumn')}
        </Text>
        <Text color="text.secondary" numberOfLines={1} variant="bodyXSmall">
          {t('calendar.planner.compact.periodSummary', { count: visibleLoanCount })}
        </Text>
      </Column>
      {calendarGroups.map(({ group, count, listings }) => (
        <Column key={group}>
          <GroupHeading count={count} group={group} />
          {listings.map(({ id, loans: listingLoans, minimumHeight }) => (
            <ListingDetails
              key={id}
              detailsWidth={detailsWidth}
              loans={listingLoans}
              minimumHeight={minimumHeight}
              onHeight={(height) => onRowHeight(id, height)}
            />
          ))}
        </Column>
      ))}
      {!calendarGroups.length ? <View style={{ height: 180 }} /> : null}
    </Column>
  );
}

export function ResponsiveLoanPlanner({ loans, loanType }: ResponsiveLoanPlannerProps) {
  const theme = useTheme<Theme>();
  const { t } = useTranslation('loans');
  const today = startOfDay(new Date());
  const [containerWidth, setContainerWidth] = useState(0);
  const scrollX = useSharedValue(0);
  const trackedPage = useSharedValue(CALENDAR_INITIAL_PAGE);
  const scrollReady = useSharedValue(false);
  const pagerGeneration = useSharedValue(0);
  const [generation, setGeneration] = useState(0);
  const [initialPage, setInitialPage] = useState(CALENDAR_INITIAL_PAGE);
  const currentGeneration = useRef(0);
  const [rowHeights, setRowHeights] = useState<Record<string, number>>({});
  const [anchorDate] = useState(today);
  const [resizedOrigin, setResizedOrigin] = useState<Date | null>(null);
  const [activePage, setActivePage] = useState(CALENDAR_INITIAL_PAGE);
  const contentContainerStyle = { paddingBottom: theme.spacing['spacing.general.x10'] };
  const { detailsWidth, visibleWeeks } = getResponsivePlannerLayout(containerWidth);
  const visibleDayCount = containerWidth > 0 && containerWidth < 520 ? 5 : visibleWeeks * 7;
  const origin =
    resizedOrigin ??
    getVisiblePlannerRangeStart(startOfWeek(anchorDate, { weekStartsOn: 1 }), visibleDayCount, anchorDate);
  const visibleRangeStart = addDays(origin, (activePage - CALENDAR_INITIAL_PAGE) * visibleDayCount);
  const timelineWidth = Math.max(0, containerWidth - detailsWidth);
  const dayWidth = timelineWidth / visibleDayCount;

  useLayoutEffect(() => {
    scrollX.set(initialPage * timelineWidth);
    trackedPage.set(initialPage);
    scrollReady.set(false);
  }, [generation, initialPage, timelineWidth, scrollX, trackedPage, scrollReady]);

  // Scroll worklets may already have queued a JS update when navigation or a
  // resize replaces the list. Never apply a selection from a retired viewport.
  const selectPage = (page: number, sourceGeneration: number) => {
    if (currentGeneration.current === sourceGeneration) setActivePage(page);
  };
  const restartPager = (page: number) => {
    const nextGeneration = currentGeneration.current + 1;
    currentGeneration.current = nextGeneration;
    pagerGeneration.set(nextGeneration);
    scrollReady.set(false);
    trackedPage.set(page);
    setInitialPage(page);
    setActivePage(page);
    setGeneration(nextGeneration);
  };

  const trackScroll = useAnimatedScrollHandler((event) => {
    if (timelineWidth <= 0 || pagerGeneration.value !== generation) return;
    const offset = event.contentOffset.x;
    if (event.layoutMeasurement?.width > 0 && Math.abs(event.layoutMeasurement.width - timelineWidth) > 1) return;
    // Remounting on resize can emit nonzero offsets before initialScrollIndex.
    // Accept scroll selection only after the requested page has been reached.
    if (!scrollReady.value && Math.abs(offset - trackedPage.value * timelineWidth) > 1) return;
    scrollReady.set(true);
    scrollX.set(offset);
    const nextPage = Math.max(0, Math.min(CALENDAR_PAGE_COUNT - 1, Math.round(offset / timelineWidth)));
    if (nextPage !== trackedPage.value) {
      trackedPage.set(nextPage);
      scheduleOnRN(selectPage, nextPage, generation);
    }
  });

  const dates = Array.from({ length: visibleDayCount }, (_, index) => addDays(visibleRangeStart, index));

  const rangeEnd = dates[dates.length - 1]!;
  const getPage = (pageIndex: number): PlannerPage => {
    const pageStart = addDays(origin, (pageIndex - CALENDAR_INITIAL_PAGE) * visibleDayCount);
    const pageDates = Array.from({ length: visibleDayCount }, (_, index) => addDays(pageStart, index));
    const visibleLoans = loans.filter((loan) => pageDates.some((date) => loanOccursOnDate(loan, date, today)));

    const groups = (() => {
      const byGroup = new Map<PlannerGroup, Loan[][]>(PLANNER_GROUPS.map((group) => [group, []]));

      groupLoansByListing(visibleLoans).forEach((listingLoans) => {
        byGroup.get(getDominantGroup(listingLoans))?.push(listingLoans);
      });

      return [...byGroup.entries()].filter((entry): entry is [PlannerGroup, Loan[][]] => entry[1].length > 0);
    })();

    const calendarGroups = groups.map(([group, groupedListings]) => ({
      group,
      count: groupedListings.reduce((count, listingLoans) => count + listingLoans.length, 0),
      listings: groupedListings.map((listingLoans) => {
        const id = getLoanDisplayListing(listingLoans[0]!)?.id ?? listingLoans[0]!.listingId;
        const minimumHeight = Math.max(
          getSizing(theme, 'sizing.general.x40'),
          listingLoans.length *
            Math.min(getSizing(theme, 'sizing.general.x18'), getSizing(theme, 'sizing.general.x14')),
        );
        return { id, loans: listingLoans, minimumHeight, height: Math.max(minimumHeight, rowHeights[id] ?? 0) };
      }),
    }));

    return { dates: pageDates, groups: calendarGroups, visibleLoans };
  };
  const currentPage = getPage(activePage);
  const calendarGroups = currentPage.groups;
  const visibleLoans = currentPage.visibleLoans;

  const gridHeight =
    56 +
    (calendarGroups.length
      ? calendarGroups.reduce(
          (total, { listings }) =>
            total + getSizing(theme, 'sizing.general.x12') + listings.reduce((sum, { height }) => sum + height, 0),
          0,
        )
      : 180);
  const gridStyle = useAnimatedStyle(
    () => ({
      height: withTiming(gridHeight, { duration: 240, reduceMotion: ReduceMotion.System }),
    }),
    [gridHeight],
  );

  const changeRange = (nextStart: Date) => {
    const index = Math.max(
      0,
      Math.min(
        CALENDAR_PAGE_COUNT - 1,
        CALENDAR_INITIAL_PAGE + Math.round(differenceInCalendarDays(nextStart, origin) / visibleDayCount),
      ),
    );
    // Mount the requested page immediately. Imperative scrollToIndex can land
    // in an unrendered spacer after repeated direction changes on web.
    restartPager(index);
  };
  const settleScrollRange = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (currentGeneration.current !== generation) return;
    if (
      event.nativeEvent.layoutMeasurement?.width > 0 &&
      Math.abs(event.nativeEvent.layoutMeasurement.width - timelineWidth) > 1
    )
      return;
    if (!scrollReady.get() && Math.abs(event.nativeEvent.contentOffset.x - trackedPage.get() * timelineWidth) > 1)
      return;
    scrollReady.set(true);
    if (timelineWidth > 0) setActivePage(getCalendarPageIndex(event.nativeEvent.contentOffset.x, timelineWidth));
  };
  const finishDrag = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    // A drag that lands exactly on a page can end without momentum. Otherwise
    // leave selection unchanged until settling; interrupted gestures still use
    // their absolute offset, so they never depend on a previous callback.
    const { contentOffset, velocity } = event.nativeEvent;
    const page = getCalendarPageIndex(contentOffset.x, timelineWidth);
    if (!velocity?.x && Math.abs(contentOffset.x - page * timelineWidth) < 1) settleScrollRange(event);
  };

  const renderPage = ({ item: pageIndex }: { item: number }) => (
    <PlannerTimelinePage
      page={getPage(pageIndex)}
      pageIndex={pageIndex}
      isActive={pageIndex === activePage}
      timelineWidth={timelineWidth}
      dayWidth={dayWidth}
      today={today}
      loanType={loanType}
      scrollX={scrollX}
    />
  );

  return (
    <ScrollView contentContainerStyle={contentContainerStyle} showsVerticalScrollIndicator={false} style={{ flex: 1 }}>
      <Column
        backgroundColor="surface.default"
        borderColor="surface.disabled"
        borderWidth={theme.borderWidths['border-width.subtle']}
        onLayout={(event) => {
          const width = Math.round(event.nativeEvent.layout.width);
          if (width <= 0 || width === containerWidth) return;
          if (containerWidth > 0) {
            setResizedOrigin(visibleRangeStart);
            restartPager(CALENDAR_INITIAL_PAGE);
          }
          setContainerWidth(width);
        }}
        style={{
          boxShadow: `0px 4px 16px ${theme.colors['shadow.lg']}`,
          overflow: 'hidden',
        }}
        testID="responsive-loan-planner"
      >
        <PlannerRangeNavigation
          visibleRangeStart={visibleRangeStart}
          rangeEnd={rangeEnd}
          onPrevious={() => changeRange(addDays(visibleRangeStart, -visibleDayCount))}
          onToday={() => {
            const nextOrigin = getVisiblePlannerRangeStart(
              startOfWeek(today, { weekStartsOn: 1 }),
              visibleDayCount,
              today,
            );
            setResizedOrigin(nextOrigin);
            restartPager(CALENDAR_INITIAL_PAGE);
          }}
          onNext={() => changeRange(addDays(visibleRangeStart, visibleDayCount))}
        />

        <Reanimated.View testID="calendar-grid-container" style={[{ overflow: 'hidden' }, gridStyle]}>
          <Row alignItems="stretch" style={{ height: gridHeight }}>
            <PlannerListingColumn
              calendarGroups={calendarGroups}
              visibleLoanCount={visibleLoans.length}
              detailsWidth={detailsWidth}
              onRowHeight={(id, height) =>
                setRowHeights((previous) => (previous[id] === height ? previous : { ...previous, [id]: height }))
              }
            />
            {timelineWidth > 0 ? (
              <Reanimated.FlatList
                key={`${timelineWidth}-${visibleDayCount}-${origin.getTime()}-${generation}`}
                data={CALENDAR_PAGES}
                renderItem={renderPage}
                keyExtractor={(index) => String(index)}
                getItemLayout={(_, index) => ({ length: timelineWidth, offset: index * timelineWidth, index })}
                initialScrollIndex={initialPage}
                // Each item contains a whole calendar. Prepare adjacent periods without
                // mounting several complete loan timelines in the same JS frame.
                initialNumToRender={1}
                maxToRenderPerBatch={3}
                windowSize={3}
                updateCellsBatchingPeriod={16}
                removeClippedSubviews={false}
                horizontal
                nestedScrollEnabled
                directionalLockEnabled
                testID="calendar-grid-scroll"
                showsHorizontalScrollIndicator={false}
                pagingEnabled
                snapToInterval={timelineWidth}
                disableIntervalMomentum
                decelerationRate="fast"
                onScroll={trackScroll}
                scrollEventThrottle={16}
                onScrollEndDrag={finishDrag}
                onMomentumScrollEnd={settleScrollRange}
                style={{ width: timelineWidth, height: gridHeight, flexGrow: 0, flexShrink: 0 }}
              />
            ) : null}
          </Row>
        </Reanimated.View>

        <Row flexWrap="wrap" gap="spacing.general.x4" padding="spacing.general.x4">
          {[
            { color: theme.colors['surface.info.default'], label: t('calendar.legend.scheduled') },
            { color: theme.colors['surface.success.default'], label: t('calendar.legend.ongoing') },
            { color: theme.colors['surface.warning.default'], label: t('calendar.legend.returned') },
            { color: theme.colors['surface.error.default'], label: t('calendar.legend.late') },
          ].map(({ color, label }) => (
            <Row alignItems="center" gap="spacing.general.x1" key={label}>
              <View
                style={{
                  backgroundColor: color,
                  borderRadius: theme.borderRadii['radius.full'],
                  height: theme.spacing['spacing.general.x2'],
                  width: theme.spacing['spacing.general.x2'],
                }}
              />

              <Text color="text.secondary" variant="bodyXSmall">
                {label}
              </Text>
            </Row>
          ))}
        </Row>
      </Column>
    </ScrollView>
  );
}
